import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { showSuccess, showError } from '../utils/notifications.js'

// Convert snake_case to camelCase (for matching fieldLabels after humps transformation)
const snakeToCamel = (str) => str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

// AI Mapping progress messages
const AI_MAPPING_MESSAGES = {
  receiving: [
    "Receiving data to analyze...",
    "Preparing file for AI analysis...",
    "Loading Excel data...",
    "Connecting to AI service...",
    "Initializing smart mapping...",
    "Reading your data..."
  ],
  reading: [
    "Reading Excel headers...",
    "Analyzing column names...",
    "Detecting data types...",
    "Scanning sample values...",
    "Understanding file structure...",
    "Identifying patterns..."
  ],
  analyzing: [
    "AI is analyzing relationships...",
    "Finding best matches...",
    "Comparing field semantics...",
    "Evaluating mapping options...",
    "Calculating confidence scores...",
    "Understanding data context..."
  ],
  mapping: [
    "Creating smart mappings...",
    "Matching columns to fields...",
    "Optimizing field assignments...",
    "Building mapping structure...",
    "Validating mappings...",
    "Finalizing suggestions..."
  ],
  finalizing: [
    "Please wait a little bit...",
    "Almost done...",
    "A little more time...",
    "Finishing up...",
    "Just a moment...",
    "Nearly there..."
  ]
}

// Confidence badge component
function ConfidenceBadge({ confidence, source, translations }) {
  const config = {
    high: { class: 'bg-success', icon: 'bi-check-circle-fill', text: translations.highConfidence },
    medium: { class: 'bg-warning text-dark', icon: 'bi-exclamation-triangle-fill', text: translations.mediumConfidence },
    low: { class: 'bg-danger', icon: 'bi-exclamation-circle-fill', text: translations.lowConfidence }
  }
  
  const { class: badgeClass, icon, text } = config[confidence] || config.low
  
  // Special text for different sources
  let displayText = text
  let displayIcon = icon
  
  if (source === 'previous_import') {
    displayText = translations.previousImport
  } else if (source === 'ai') {
    displayIcon = 'bi-lightbulb-fill' // AI lightbulb icon
    displayText = `${text} with AI`
  }
  
  return (
    <span className={`badge ${badgeClass}`}>
      <i className={`bi ${displayIcon} me-1`}></i>
      {displayText}
    </span>
  )
}

// Field mapping section component (tenant-centric)
// A column is identified by its INDEX. Rent rolls repeat headings ("MIETE" over
// both a monthly and an annual column), so the label cannot be the key.
function FieldMappingSection({ title, icon, fields, headers, sampleRow, fieldMappings, onMappingChange, translations }) {
  const usedColumns = new Set(
    Object.values(fieldMappings)
      .map((mapping) => mapping.index)
      .filter((index) => index !== null && index !== undefined)
  )

  return (
    <div className="mapping-section-card">
      <div className="mapping-section-card-header">
        <i className={`bi ${icon} me-2`}></i>
        <span className="mapping-section-title text-uppercase fw-semibold">{title}</span>
        <span className="badge bg-primary ms-auto">{fields.length} {translations.fields}</span>
      </div>
      <div className="mapping-section-card-body">
        <table className="table table-sm mb-0">
          <thead>
            <tr className="text-muted small text-uppercase">
              <th style={{ width: '25%', fontWeight: 500 }}>{translations.systemField}</th>
              <th style={{ width: '30%', fontWeight: 500 }}>{translations.mappedField}</th>
              <th style={{ width: '25%', fontWeight: 500 }}>{translations.sampleData}</th>
              <th style={{ width: '20%', fontWeight: 500 }}>{translations.confidence}</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field, index) => {
              const mapping = fieldMappings[field.field] || {}
              const currentIndex = mapping.index ?? null
              const currentValue = currentIndex === null ? '' : String(currentIndex)
              const sampleValue = currentIndex === null ? null : sampleRow[currentIndex]
              
              return (
                <tr key={index}>
                  <td className="align-middle">
                    <span className="fw-medium">{translations.fieldLabels?.[snakeToCamel(field.field)] || field.field}</span>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={currentValue}
                      onChange={(e) => onMappingChange(field.field, e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">{translations.selectColumn}</option>
                      {headers.map((header, columnIndex) => header && (
                        <option
                          key={columnIndex}
                          value={columnIndex}
                          disabled={columnIndex !== currentIndex && usedColumns.has(columnIndex)}
                        >
                          {header}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="align-middle">
                    {sampleValue !== null && sampleValue !== undefined ? (
                      <span className="text-muted fst-italic text-truncate d-block" style={{ maxWidth: '200px' }}>
                        {String(sampleValue)}
                      </span>
                    ) : (
                      <span className="text-muted fst-italic">—</span>
                    )}
                  </td>
                  <td className="align-middle">
                    {currentIndex !== null && (
                      <ConfidenceBadge 
                        confidence={field.confidence} 
                        source={field.source} 
                        translations={translations} 
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Password Modal component
function PasswordModal({ isOpen, onClose, onSubmit, error, onClearError, translations }) {
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!password) return
    setIsSubmitting(true)
    await onSubmit(password)
    setIsSubmitting(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit()
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
    if (error) onClearError()
  }

  const handleClose = () => {
    setPassword('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{translations.title}</h5>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>
          <div className="modal-body">
            <p className="text-muted">{translations.description}</p>
            {error && (
              <div className="alert alert-danger py-2">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">{translations.passwordLabel}</label>
              <input
                type="password"
                className={`form-control ${error ? 'is-invalid' : ''}`}
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyDown}
                placeholder={translations.passwordPlaceholder}
                autoComplete="new-password"
                data-form-type="other"
                data-lpignore="true"
                autoFocus
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={isSubmitting}>
              {translations.cancel}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={!password || isSubmitting}>
              {isSubmitting ? 'Loading...' : translations.submit}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Mapping Modal component (tenant-centric)
function MappingModal({ isOpen, onClose, onSubmit, data, filename, fieldMappings, onMappingChange, suspiciousRows, excludedSuspiciousRows, onToggleSuspiciousRow, isAiMapping, aiMappingMessage, isBubbleFadingOut, onAiAutomap, rentUnit, onRentUnitChange, translations }) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen || !data) return null

  const { required_fields, optional_fields, headers, sample_row, detected_locale, available_locales, available_rent_units, suspicious_stats } = data

  // Find locale label
  const localeLabel = available_locales?.find(l => l.value === detected_locale)?.label || detected_locale?.toUpperCase()

  // Calculate actual rows to import
  const normalRowsCount = data.total_rows || 0
  const includedSuspiciousCount = suspiciousRows ? suspiciousRows.filter(r => !excludedSuspiciousRows.has(r.index)).length : 0
  const totalToImport = normalRowsCount + includedSuspiciousCount

  const handleSubmit = async () => {
    setIsSubmitting(true)
    await onSubmit()
    setIsSubmitting(false)
  }

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">{translations.title}</h5>
              <small className="text-muted">
                {translations.reviewing.replace('%{count}', totalToImport).replace('%{filename}', filename)}
                {detected_locale && (
                  <span className="ms-2">
                    <i className="bi bi-translate me-1"></i>
                    <strong>{localeLabel}</strong>
                  </span>
                )}
              </small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body mapping-modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Suspicious rows warning */}
            {suspiciousRows && suspiciousRows.length > 0 && (
              <SuspiciousRowsSection
                suspiciousRows={suspiciousRows}
                excludedRows={excludedSuspiciousRows}
                onToggleRow={onToggleSuspiciousRow}
                translations={translations.suspicious || {}}
              />
            )}
            
            <div className="d-flex flex-column gap-4">
              {required_fields && required_fields.length > 0 && (
                <FieldMappingSection 
                  title={translations.requiredFields}
                  icon="bi-check2-square"
                  fields={required_fields}
                  headers={headers}
                  sampleRow={sample_row || {}}
                  fieldMappings={fieldMappings}
                  onMappingChange={onMappingChange}
                  translations={translations}
                />
              )}
              {optional_fields && optional_fields.length > 0 && (
                <FieldMappingSection 
                  title={translations.optionalFields}
                  icon="bi-list-ul"
                  fields={optional_fields}
                  headers={headers}
                  sampleRow={sample_row || {}}
                  fieldMappings={fieldMappings}
                  onMappingChange={onMappingChange}
                  translations={translations}
                />
              )}

              {/* Rent unit applies to the whole file: rent rolls state the
                  period in the column heading, not in a column of its own. */}
              {available_rent_units && available_rent_units.length > 0 && (
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label mb-0 fw-semibold" htmlFor="rent-roll-rent-unit">
                    {translations.rentUnitLabel}
                  </label>
                  <select
                    id="rent-roll-rent-unit"
                    className="form-select w-auto"
                    value={rentUnit}
                    onChange={(e) => onRentUnitChange(e.target.value)}
                  >
                    {available_rent_units.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                  <small className="text-muted">{translations.rentUnitHint}</small>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer flex-column align-items-stretch">
            <div className="d-flex">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSubmitting}>
                {translations.cancel}
              </button>
              <div className="flex-grow-1"></div>
              <div style={{ position: 'relative' }}>
                {aiMappingMessage && (
                  <div 
                       className="ai-mapping-bubble" 
                       style={{ 
                         position: 'absolute',
                         bottom: 'calc(100% + 15px)',
                         left: '50%',
                         transform: 'translateX(-50%)',
                         padding: '12px 20px',
                         backgroundColor: '#ffc107',
                         borderRadius: '20px',
                         fontSize: '0.9rem',
                         fontWeight: '500',
                         color: '#000',
                         whiteSpace: 'nowrap',
                         boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                         animation: isBubbleFadingOut 
                           ? 'bubbleFadeOut 0.3s ease-out forwards' 
                           : 'bubblePop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                         zIndex: 1000
                       }}>
                    {aiMappingMessage}
                    <div style={{
                      content: '""',
                      position: 'absolute',
                      bottom: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '10px solid transparent',
                      borderRight: '10px solid transparent',
                      borderTop: '10px solid #ffc107'
                    }}></div>
                  </div>
                )}
                <button 
                  type="button" 
                  className="btn btn-outline-primary me-2" 
                  onClick={onAiAutomap}
                  disabled={isSubmitting || isAiMapping}
                >
                  {isAiMapping ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>AI Mapping...</>
                  ) : (
                    <><i className="bi bi-lightbulb-fill me-1"></i>Automap with AI</>
                  )}
                </button>
              </div>
              <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>Importing...</>
                ) : (
                  <><i className="bi bi-cloud-upload me-1"></i>{translations.confirmImport} ({totalToImport})</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Success Modal component
function SuccessModal({ isOpen, result, onComplete, translations }) {
  if (!isOpen || !result) return null

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center py-5">
            <div className="rent-roll-success-icon mb-4">
              <i className="bi bi-check-circle-fill text-success"></i>
            </div>
            <h4 className="mb-2">{translations.title}</h4>
            <p className="text-muted mb-4">{translations.message}</p>
            <div className="d-flex justify-content-center gap-4 mb-4">
              <div className="rent-roll-stat">
                <span className="rent-roll-stat-value">{result.total}</span>
                <span className="rent-roll-stat-label">{translations.totalFound}</span>
              </div>
              <div className="rent-roll-stat rent-roll-stat--success">
                <span className="rent-roll-stat-value">{result.imported}</span>
                <span className="rent-roll-stat-label">{translations.imported}</span>
              </div>
              <div className="rent-roll-stat rent-roll-stat--warning">
                <span className="rent-roll-stat-value">{result.skipped}</span>
                <span className="rent-roll-stat-label">{translations.skipped}</span>
              </div>
            </div>
            <button type="button" className="btn btn-primary btn-lg w-100" onClick={onComplete}>
              {translations.complete}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Map reason text to Bootstrap icon
function getReasonIcon(reason) {
  if (reason.includes('section header')) return 'bi-h-square'
  if (reason.includes('Total')) return 'bi-calculator'
  if (reason.includes('bold')) return 'bi-type-bold'
  if (reason.includes('font size')) return 'bi-fonts'
  if (reason.includes('background')) return 'bi-stop-btn-fill'
  if (reason.includes('text color')) return 'bi-textarea-t'
  if (reason.includes('data type')) return 'bi-exclamation-triangle'
  return 'bi-question-circle'
}

// Suspicious Rows Section component
function SuspiciousRowsSection({ suspiciousRows, excludedRows, onToggleRow, translations }) {
  if (!suspiciousRows || suspiciousRows.length === 0) return null

  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="alert alert-warning mb-4">
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>{translations.suspiciousTitle || 'Suspicious rows detected'}</strong>
          <span className="text-muted ms-2">
            ({suspiciousRows.length} {translations.rowsNeedReview || 'rows may need review'})
          </span>
        </div>
        <button 
          type="button" 
          className="btn btn-sm btn-outline-warning"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <><i className="bi bi-chevron-up me-1"></i>{translations.collapse || 'Collapse'}</>
          ) : (
            <><i className="bi bi-chevron-down me-1"></i>{translations.review || 'Review'}</>
          )}
        </button>
      </div>
      
      {isExpanded && (
        <div className="mt-3">
          <p className="small text-muted mb-2">
            {translations.suspiciousDescription || 'These rows appear to be totals, separators, or have unusual structure. They are excluded by default.'}
          </p>
          <div className="table-responsive" style={{ maxHeight: '250px', overflowY: 'auto' }}>
            <table className="table table-sm table-bordered mb-0 bg-white">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: '50px' }}>{translations.include || 'Include'}</th>
                  <th style={{ width: '80px' }}>{translations.row || 'Row'}</th>
                  <th>{translations.preview || 'Preview'}</th>
                  <th style={{ width: '200px' }}>{translations.reason || 'Reason'}</th>
                </tr>
              </thead>
              <tbody>
                {suspiciousRows.map((row, idx) => (
                  <tr key={idx} className={excludedRows.has(row.index) ? '' : 'table-success'}>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!excludedRows.has(row.index)}
                        onChange={() => onToggleRow(row.index)}
                      />
                    </td>
                    <td className="text-muted small">#{row.index + 1}</td>
                    <td className="small" style={{ maxWidth: '300px' }}>
                      <div className="text-truncate">{row.preview}</div>
                      {row.confidence < 30 && (
                        <div className="badge bg-success-subtle text-success mt-1" style={{ fontSize: '0.7rem' }}>
                          Looks like data. Maybe better to include it?
                        </div>
                      )}
                    </td>
                    <td className="small text-muted">
                      {row.reasons.map((reason, i) => (
                        <div key={i}>
                          <i className={`bi ${getReasonIcon(reason)} me-1`}></i>
                          {reason}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// Language Select Modal component
function LanguageSelectModal({ isOpen, onClose, onSubmit, detectedLocale, availableLocales, translations }) {
  const [selectedLocale, setSelectedLocale] = useState(detectedLocale || 'en')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    setIsSubmitting(true)
    await onSubmit(selectedLocale)
    setIsSubmitting(false)
  }

  if (!isOpen) return null

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-translate me-2"></i>
              {translations.title}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              {translations.description}
            </div>
            <div className="mb-3">
              <label className="form-label">{translations.selectLabel}</label>
              <select
                className="form-select"
                value={selectedLocale}
                onChange={(e) => setSelectedLocale(e.target.value)}
              >
                {availableLocales && availableLocales.map(locale => (
                  <option key={locale.value} value={locale.value}>
                    {locale.label}
                  </option>
                ))}
              </select>
              <div className="form-text">{translations.hint}</div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
              {translations.cancel}
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>{translations.analyzing}</>
              ) : (
                <><i className="bi bi-check-lg me-1"></i>{translations.confirm}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Main component
export default function RentRollImport({ uploadUrl, analyzeWithLocaleUrl, aiAutomapUrl, importUrl, tenantsUrl, createTenantUrl, csrfToken, translations }) {
  const [isLoading, setIsLoading] = useState(false)
  const [pendingFile, setPendingFile] = useState(null)
  const [parsedData, setParsedData] = useState(null)
  // fieldMappings: { fieldName: { column: 'Excel Header' } }
  const [fieldMappings, setFieldMappings] = useState({})
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [showLanguageSelectModal, setShowLanguageSelectModal] = useState(false)
  const [showMappingModal, setShowMappingModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [importResult, setImportResult] = useState(null)
  // Suspicious rows: excluded by default
  const [excludedSuspiciousRows, setExcludedSuspiciousRows] = useState(new Set())
  // AI automap state
  const [isAiMapping, setIsAiMapping] = useState(false)
  // EVA-272: rent rolls state the rent period in the column heading, so the unit
  // is chosen once for the whole file. Pre-filled from the backend suggestion.
  const [rentUnit, setRentUnit] = useState('PER_MONTH')
  const [aiMappingMessage, setAiMappingMessage] = useState('')
  const [isBubbleFadingOut, setIsBubbleFadingOut] = useState(false)

  // Build initial field mappings from backend response
  const buildInitialMappings = (requiredFields, optionalFields) => {
    const mappings = {}
    const allFields = [...(requiredFields || []), ...(optionalFields || [])]
    
    allFields.forEach(field => {
      mappings[field.field] = { index: field.mapped_index ?? null }
    })
    
    return mappings
  }

  const uploadFile = useCallback(async (file, password = null) => {
    const formData = new FormData()
    formData.append('file', file)
    if (password) formData.append('password', password)

    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json' },
      body: formData
    })
    return response.json()
  }, [uploadUrl, csrfToken])

  const processFile = useCallback(async (file, password = null) => {
    setIsLoading(true)
    setPendingFile(file)
    const startTime = Date.now()

    try {
      const result = await uploadFile(file, password)
      
      // Ensure loading shows for at least 1 second to avoid flicker
      const elapsed = Date.now() - startTime
      if (elapsed < 1000) {
        await new Promise(resolve => setTimeout(resolve, 1000 - elapsed))
      }
      
      setIsLoading(false)

      if (result.error === 'password_required') {
        setPasswordError(null)
        setShowPasswordModal(true)
      } else if (result.error === 'invalid_password') {
        // Show error in password modal instead of closing it
        setPasswordError(translations.errors.invalid_password || translations.errors.invalidPassword)
        setShowPasswordModal(true)
      } else if (result.error) {
        setShowPasswordModal(false)
        showError(translations.errors[result.error] || result.error)
      } else {
        // Success - close password modal if open
        setShowPasswordModal(false)
        setPasswordError(null)
        
        // Store parsed data
        setParsedData(result)
        setRentUnit(result.suggested_rent_unit || 'PER_MONTH')
        
        // Check if language detection was reliable
        if (!result.locale_reliable) {
          // Show language selection modal
          setShowLanguageSelectModal(true)
        } else {
          // Language is reliable - show mapping modal directly
          const initialMappings = buildInitialMappings(result.required_fields, result.optional_fields)
          setFieldMappings(initialMappings)
          
          // Initialize excluded suspicious rows (all excluded by default)
          if (result.suspicious_rows && result.suspicious_rows.length > 0) {
            setExcludedSuspiciousRows(new Set(result.suspicious_rows.map(r => r.index)))
          } else {
            setExcludedSuspiciousRows(new Set())
          }
          
          setShowMappingModal(true)
        }
      }
    } catch (error) {
      setIsLoading(false)
      showError(translations.errors.uploadFailed + ': ' + error.message)
    }
  }, [uploadFile, translations])

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) processFile(acceptedFiles[0])
  }, [processFile])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] },
    maxFiles: 1,
    multiple: false
  })

  const handlePasswordSubmit = async (password) => {
    // Don't close modal here - processFile will handle it based on result
    await processFile(pendingFile, password)
  }

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false)
    setPasswordError(null)
  }

  const handleLocaleSelect = async (locale) => {
    setShowLanguageSelectModal(false)
    setIsLoading(true)

    try {
      const response = await fetch(analyzeWithLocaleUrl, {
        method: 'POST',
        headers: { 
          'X-CSRF-Token': csrfToken, 
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          headers: JSON.stringify(parsedData.headers),
          rows: JSON.stringify(parsedData.rows),
          locale: locale
        })
      })

      const result = await response.json()
      setIsLoading(false)

      if (result.error) {
        showError(translations.errors[result.error] || result.error)
      } else {
        // Update parsed data with new field mappings. detected_locale must be
        // refreshed too — the AI automap call and the import both read it, and
        // the manually chosen language is now the authoritative one.
        setParsedData(prev => ({
          ...prev,
          required_fields: result.required_fields,
          optional_fields: result.optional_fields,
          sample_row: result.sample_row,
          detected_locale: result.detected_locale || locale
        }))
        setRentUnit(result.suggested_rent_unit || 'PER_MONTH')

        // Set initial mappings
        const initialMappings = buildInitialMappings(result.required_fields, result.optional_fields)
        setFieldMappings(initialMappings)
        
        // Keep existing excluded suspicious rows (they were set during initial upload)
        setShowMappingModal(true)
      }
    } catch (error) {
      setIsLoading(false)
      showError(translations.errors.analyzeFailed + ': ' + error.message)
    }
  }

  // Handle mapping change: field -> column index
  const handleMappingChange = (field, columnIndex) => {
    setFieldMappings(prev => ({
      ...prev,
      [field]: { index: columnIndex ?? null }
    }))
  }

  // Toggle suspicious row inclusion
  const handleToggleSuspiciousRow = (rowIndex) => {
    setExcludedSuspiciousRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex)
      } else {
        newSet.add(rowIndex)
      }
      return newSet
    })
  }

  const handleAiAutomap = async () => {
    if (!parsedData || !aiAutomapUrl) return
    
    setIsAiMapping(true)
    
    // Helper to show random message from block
    const showRandomMessage = (block) => {
      const messages = AI_MAPPING_MESSAGES[block]
      const randomMsg = messages[Math.floor(Math.random() * messages.length)]
      setAiMappingMessage(randomMsg)
    }
    
    // Progressive messages timeline
    const progressTimers = [
      setTimeout(() => showRandomMessage('receiving'), 2000),    // +2s
      setTimeout(() => showRandomMessage('reading'), 6000),      // +4s
      setTimeout(() => showRandomMessage('analyzing'), 10000),   // +4s
      setTimeout(() => showRandomMessage('mapping'), 14000),     // +4s
      setTimeout(() => showRandomMessage('finalizing'), 18000),  // +4s
    ]
    
    try {
      const response = await fetch(aiAutomapUrl, {
        method: 'POST',
        headers: { 
          'X-CSRF-Token': csrfToken, 
          'Accept': 'application/json', 
          'Content-Type': 'application/json' 
        },
        // EVA-244: cell values must not reach the inference provider. The rows
        // go to OUR server, which derives the column profile itself and puts
        // only type tags and counters in the prompt — the guarantee no longer
        // depends on the browser sending a well-formed profile.
        body: JSON.stringify({
          headers: JSON.stringify(parsedData.headers),
          rows: JSON.stringify(parsedData.rows),
          locale: parsedData.detected_locale || 'en'
        })
      })

      // Clear all timers
      progressTimers.forEach(timer => clearTimeout(timer))

      if (!response.ok) {
        const errorData = await response.json()
        alert(errorData.error || 'AI mapping failed')
        return
      }

      const aiResult = await response.json()
      
      // Update parsedData with AI results
      setParsedData(prev => ({
        ...prev,
        required_fields: aiResult.required_fields,
        optional_fields: aiResult.optional_fields
      }))
      
      // Rebuild field mappings from AI results
      const newMappings = buildInitialMappings(aiResult.required_fields, aiResult.optional_fields)
      setFieldMappings(newMappings)
      
      // Show success message briefly before hiding
      setAiMappingMessage('✓ Mapping completed!')
      await new Promise(resolve => setTimeout(resolve, 800))
      
      // Start fade out
      setIsBubbleFadingOut(true)
      await new Promise(resolve => setTimeout(resolve, 300))
      
    } catch (error) {
      // Clear timers on error
      progressTimers.forEach(timer => clearTimeout(timer))
      console.error('AI automap error:', error)
      alert('AI mapping failed. Please try again.')
      
      // Fade out on error too
      setIsBubbleFadingOut(true)
      await new Promise(resolve => setTimeout(resolve, 300))
    } finally {
      setIsAiMapping(false)
      setAiMappingMessage('')
      setIsBubbleFadingOut(false)
    }
  }

  const handleImportSubmit = async () => {
    setShowMappingModal(false)
    setIsLoading(true)

    // Backend format: column index -> field
    const mapping = {}
    Object.entries(fieldMappings).forEach(([field, { index }]) => {
      if (index !== null && index !== undefined) {
        mapping[String(index)] = field
      }
    })

    // Combine normal rows with included suspicious rows, carrying the
    // spreadsheet row number with each so import errors can name it.
    let rowsToImport = [...parsedData.rows]
    let rowNumbers = [...(parsedData.row_numbers || [])]
    if (parsedData.suspicious_rows && parsedData.suspicious_rows.length > 0) {
      const included = parsedData.suspicious_rows.filter(sr => !excludedSuspiciousRows.has(sr.index))
      rowsToImport = [...rowsToImport, ...included.map(sr => sr.row)]
      rowNumbers = [...rowNumbers, ...included.map(sr => sr.excel_row)]
    }

    try {
      const response = await fetch(importUrl, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken, 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mapping: JSON.stringify(mapping),
          rows: JSON.stringify(rowsToImport),
          headers: JSON.stringify(parsedData.headers),
          row_numbers: JSON.stringify(rowNumbers),
          locale: parsedData.detected_locale || 'en',
          rent_unit: rentUnit
        })
      })

      const result = await response.json()
      setIsLoading(false)

      if (result.success) {
        setImportResult(result.result)
        setShowSuccessModal(true)
      } else {
        showError(translations.errors[result.error] || result.error)
      }
    } catch (error) {
      setIsLoading(false)
      showError(translations.errors.importFailed + ': ' + error.message)
    }
  }

  const handleComplete = () => {
    setShowSuccessModal(false)
    window.location.href = tenantsUrl
  }

  return (
    <div className="p-4">
      <div className="rent-roll-options-container">
        <div className="row g-4">
          <div className="col-md-6">
            <div className="rent-roll-option-card h-100">
              <div className="text-center p-4">
                <div className="rent-roll-option-icon mb-3"><i className="bi bi-building"></i></div>
                <h5 className="mb-2">{translations.noTenantsTitle}</h5>
                <p className="text-muted mb-4">{translations.noTenantsDescription}</p>
                <form action={createTenantUrl} method="post" data-turbo="false">
                  <input type="hidden" name="authenticity_token" value={csrfToken} />
                  <button type="submit" className="btn btn-primary">{translations.addFirstTenant}</button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 d-md-none text-center">
            <span className="rent-roll-or-divider">{translations.or}</span>
          </div>

          <div className="col-md-6">
            <div {...getRootProps()} className={`rent-roll-option-card rent-roll-dropzone h-100 ${isDragActive ? 'rent-roll-dropzone--dragover' : ''}`}>
              <input {...getInputProps()} />
              <div className="text-center p-4">
                <div className="rent-roll-option-icon rent-roll-option-icon--upload mb-3"><i className="bi bi-cloud-arrow-up"></i></div>
                <h5 className="mb-2">{translations.importTitle}</h5>
                <p className="text-muted mb-4">{translations.dropHint}</p>
                <span className="btn btn-outline-primary">
                  <i className="bi bi-cloud-upload me-1"></i>{translations.uploadButton}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="rent-roll-or-divider-desktop d-none d-md-block">
          <span>{translations.or}</span>
        </div>
      </div>

      {isLoading && (
        <div className="rent-roll-loading">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
          <p className="mt-2 mb-0">{translations.processing}</p>
        </div>
      )}

      <PasswordModal 
        isOpen={showPasswordModal} 
        onClose={handlePasswordModalClose} 
        onSubmit={handlePasswordSubmit}
        error={passwordError}
        onClearError={() => setPasswordError(null)}
        translations={translations.passwordModal}
      />
      <LanguageSelectModal 
        isOpen={showLanguageSelectModal} 
        onClose={() => setShowLanguageSelectModal(false)} 
        onSubmit={handleLocaleSelect}
        detectedLocale={parsedData?.detected_locale}
        availableLocales={parsedData?.available_locales}
        translations={translations.languageSelect}
      />
      <MappingModal
        rentUnit={rentUnit}
        onRentUnitChange={setRentUnit} 
        isOpen={showMappingModal} 
        onClose={() => setShowMappingModal(false)} 
        onSubmit={handleImportSubmit} 
        data={parsedData}
        filename={pendingFile?.name || 'file'} 
        fieldMappings={fieldMappings} 
        onMappingChange={handleMappingChange} 
        suspiciousRows={parsedData?.suspicious_rows}
        excludedSuspiciousRows={excludedSuspiciousRows}
        onToggleSuspiciousRow={handleToggleSuspiciousRow}
        isAiMapping={isAiMapping}
        aiMappingMessage={aiMappingMessage}
        isBubbleFadingOut={isBubbleFadingOut}
        onAiAutomap={handleAiAutomap}
        translations={translations.mapping} 
      />
      <SuccessModal isOpen={showSuccessModal} result={importResult} onComplete={handleComplete} translations={translations.success} />
    </div>
  )
}
